import React, { useState, useEffect } from "react";
import {
  makeStyles, Paper, Button, TextField, MenuItem, FormControl, InputLabel, Select,
  IconButton, Tooltip, Drawer, Dialog, DialogTitle,
  CircularProgress, Switch, FormControlLabel, Typography, InputAdornment
} from "@material-ui/core";
import {
  Add as AddIcon, EditOutlined as EditIcon, Close as CloseIcon,
  MoreVert as MoreIcon, LayersOutlined as LayersIcon,
  PeopleOutline as PeopleIcon, Link as LinkIcon, ListAlt as QueueIcon,
  Public as PublicIcon, LockOutlined as LockIcon,
  WhatsApp as WhatsappIcon, Facebook as FacebookIcon, Instagram as InstagramIcon,
  ForumOutlined as ChatIcon, Code as ApiIcon, SendOutlined as SendIcon,
  EventOutlined as ScheduleIcon,
  ExtensionOutlined as IntegrationIcon, Android as BotFallback,
  DeleteOutline as DeleteIcon, Search as SearchIcon,
  SettingsOutlined as SettingsIcon
} from "@material-ui/icons";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import ConfirmationModal from "../ConfirmationModal";

// Apenas a interface foi reorganizada: campos e chamadas à API são os mesmos.
const BLUE = "#1047ee";
const EMPTY_PLAN = {
  name: "", users: 0, connections: 0, queues: 0, amount: 0,
  useWhatsapp: true, useFacebook: true, useInstagram: true,
  useCampaigns: true, useSchedules: true, useInternalChat: true,
  useExternalApi: true, useKanban: true, useOpenAi: true,
  useIntegrations: true, isPublic: true
};

const FEATURES = [
  { key: "useWhatsapp", label: "WhatsApp", icon: WhatsappIcon, color: "#17ad67", group: "Canais de comunicação" },
  { key: "useFacebook", label: "Facebook", icon: FacebookIcon, color: "#1877f2", group: "Canais de comunicação" },
  { key: "useInstagram", label: "Instagram", icon: InstagramIcon, color: "#dc3f93", group: "Canais de comunicação" },
  { key: "useCampaigns", label: "Disparo em Massa", icon: SendIcon, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useSchedules", label: "Agendamentos", icon: ScheduleIcon, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useInternalChat", label: "Chat Interno", icon: ChatIcon, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useExternalApi", label: "API Externa", icon: ApiIcon, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useKanban", label: "Kanban", icon: LayersIcon, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useOpenAi", label: "Talk.Ai", icon: BotFallback, color: BLUE, group: "Funcionalidades incluídas" },
  { key: "useIntegrations", label: "Integrações", icon: IntegrationIcon, color: BLUE, group: "Funcionalidades incluídas" }
];

const planForEdit = (data) => {
  const next = { ...EMPTY_PLAN, ...data };
  FEATURES.forEach(({ key }) => { next[key] = data[key] === false ? false : true; });
  return next;
};

const amountLabel = (raw) => {
  if (raw === undefined || raw === null || raw === "") return "0,00";
  const s = String(raw).trim();
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : s;
};

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", background: "transparent", color: theme.palette.text.primary },
  surface: { background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 14 },
  shell: { padding: "18px 19px", boxShadow: theme.palette.type === "dark" ? "none" : "0 9px 29px rgba(23,40,80,.035)" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 13, marginBottom: 19 },
  heading: { fontWeight: 760, fontSize: 21, margin: 0 },
  subheading: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 5 },
  primary: { backgroundColor: BLUE, color: "white", borderRadius: 8, padding: "10px 17px", minHeight: 39, fontWeight: 700, textTransform: "none", boxShadow: "0 3px 10px rgba(16,71,238,.15)", "&:hover": { background: "#0b35bc" } },
  filters: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20, alignItems: "center" },
  search: { flex: "1 1 240px", maxWidth: 440, "& .MuiOutlinedInput-root": { borderRadius: 9 } },
  selectFilter: { minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 9 } },
  cards: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14, "@media(max-width:1150px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" }, "@media(max-width:730px)": { gridTemplateColumns: "1fr" } },
  card: { minWidth: 0, minHeight: 357, height: "100%", boxSizing: "border-box", border: `1px solid ${theme.palette.divider}`, borderRadius: 13, padding: 17, background: theme.palette.background.paper, display: "flex", flexDirection: "column", transition: "border-color .15s, box-shadow .15s", "&:hover": { borderColor: "#b4c5f5", boxShadow: "0 7px 22px rgba(21,47,100,.07)" } },
  cardTop: { display: "flex", gap: 12, alignItems: "flex-start", minHeight: 80 },
  planIcon: { width: 53, height: 53, flexShrink: 0, borderRadius: "50%", background: "#e9f1ff", color: BLUE, display: "flex", alignItems: "center", justifyContent: "center" },
  cardMeta: { flex: 1, minWidth: 0 },
  planName: { fontWeight: 750, fontSize: 17, lineHeight: 1.3, overflowWrap: "anywhere", marginTop: 6 },
  planBadge: { display: "inline-flex", alignItems: "center", fontSize: 10, fontWeight: 750, borderRadius: 15, padding: "3px 9px" },
  price: { fontWeight: 800, color: BLUE, fontSize: 22, letterSpacing: "-.45px", margin: "9px 0 15px" },
  priceSuffix: { fontSize: 12, fontWeight: 500 },
  limits: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 6, marginBottom: 13 },
  limitCell: { background: theme.palette.type === "dark" ? theme.palette.background.default : "#f6f8fc", border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: "8px 4px", display: "flex", justifyContent: "center", gap: 5, alignItems: "center", minWidth: 0 },
  limitValue: { fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" },
  limitLabel: { color: theme.palette.text.secondary, fontSize: 10 },
  includedLabel: { fontSize: 11, fontWeight: 750, marginBottom: 7 },
  channelIcons: { display: "flex", gap: 10, alignItems: "center", minHeight: 27, flexWrap: "wrap" },
  actions: { marginTop: "auto", paddingTop: 14, display: "flex", gap: 8 },
  editButton: { flex: 1, background: BLUE, color: "#fff", textTransform: "none", borderRadius: 8, fontWeight: 700, "&:hover": { background: "#0b35bc" } },
  moreButton: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, width: 42, height: 36 },
  createCard: { cursor: "pointer", border: `1.5px dashed ${theme.palette.divider}`, borderRadius: 13, minHeight: 357, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 25, gap: 10, color: theme.palette.text.secondary, background: theme.palette.type === "dark" ? theme.palette.background.paper : "#fafcff", "&:hover": { borderColor: BLUE } },
  empty: { textAlign: "center", padding: 45, color: theme.palette.text.secondary, gridColumn: "1 / -1" },
  drawerPaper: { width: 480, maxWidth: "100vw", background: theme.palette.background.paper, boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "19px 22px", borderBottom: `1px solid ${theme.palette.divider}` },
  dialogPaper: { borderRadius: 15 },
  formScroll: { overflowY: "auto", flex: "1 1 auto", minHeight: 0, padding: "19px 22px", maxHeight: "calc(100vh - 190px)" },
  section: { border: `1px solid ${theme.palette.divider}`, borderRadius: 11, padding: 16, marginBottom: 13 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 750, marginBottom: 15, color: theme.palette.text.primary },
  sectionFields: { display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12, "@media(max-width:440px)": { gridTemplateColumns: "1fr" } },
  field: { width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 8 }, "& .MuiInputBase-input": { fontSize: 13 } },
  featureRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${theme.palette.divider}`, gap: 8, "&:last-child": { borderBottom: "none" } },
  featureName: { display: "flex", gap: 9, alignItems: "center", minWidth: 0, fontSize: 13 },
  footer: { padding: "13px 22px", borderTop: `1px solid ${theme.palette.divider}`, display: "flex", gap: 9, justifyContent: "space-between", alignItems: "center", background: theme.palette.background.paper },
  footRight: { display: "flex", gap: 9 },
  smallText: { color: theme.palette.text.secondary, fontSize: 12 }
}));

// A edição usa o painel lateral; a criação usa um modal centralizado.
export function PlanManagerForm({ initialValue, onSubmit, onDelete, onCancel, loading }) {
  const classes = useStyles();
  return (
    <Formik initialValues={initialValue || EMPTY_PLAN} enableReinitialize onSubmit={onSubmit}>
      {({ values, handleChange, setFieldValue }) => (
        <Form style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 120px)" }}>
          <div className={classes.formScroll}>
            <div className={classes.section}>
              <div className={classes.sectionTitle}><SettingsIcon fontSize="small" color="primary" /> Informações do plano</div>
              <div className={classes.sectionFields}>
                <TextField className={classes.field} variant="outlined" size="small" label="Nome do plano" name="name" value={values.name || ""} onChange={handleChange} required style={{ gridColumn: "1/-1" }} />
                <TextField className={classes.field} variant="outlined" size="small" label="Valor (R$)" name="amount" value={values.amount === null || values.amount === undefined ? "" : values.amount} onChange={handleChange} />
                <FormControl className={classes.field} variant="outlined" size="small">
                  <InputLabel>Visibilidade</InputLabel>
                  <Select label="Visibilidade" value={values.isPublic === false ? "private" : "public"} onChange={(event) => setFieldValue("isPublic", event.target.value === "public")}>
                    <MenuItem value="public">Público</MenuItem>
                    <MenuItem value="private">Privado</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
            <div className={classes.section}>
              <div className={classes.sectionTitle}><PeopleIcon fontSize="small" color="primary" /> Limites de utilização</div>
              <div className={classes.sectionFields}>
                <TextField className={classes.field} variant="outlined" size="small" type="number" label="Usuários" name="users" value={values.users === null || values.users === undefined ? "" : values.users} onChange={handleChange} />
                <TextField className={classes.field} variant="outlined" size="small" type="number" label="Conexões" name="connections" value={values.connections === null || values.connections === undefined ? "" : values.connections} onChange={handleChange} />
                <TextField className={classes.field} variant="outlined" size="small" type="number" label="Filas" name="queues" value={values.queues === null || values.queues === undefined ? "" : values.queues} onChange={handleChange} />
              </div>
            </div>
            {["Canais de comunicação", "Funcionalidades incluídas"].map((group) => (
              <div className={classes.section} key={group}>
                <div className={classes.sectionTitle}><LayersIcon fontSize="small" color="primary" /> {group}</div>
                {FEATURES.filter(feature => feature.group === group).map(({ key, label, icon: Icon, color }) => (
                  <div key={key} className={classes.featureRow}>
                    <span className={classes.featureName}><Icon style={{ fontSize: 19, color }} />{label}</span>
                    <FormControlLabel
                      style={{ marginRight: 0 }}
                      control={<Switch color="primary" size="small" checked={values[key] !== false} onChange={(_, checked) => setFieldValue(key, checked)} />}
                      label={<span className={classes.smallText}>{values[key] === false ? "Desabilitado" : "Habilitado"}</span>}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className={classes.footer}>
            {initialValue && initialValue.id !== undefined ? (
              <Button onClick={() => onDelete(initialValue)} disabled={loading} style={{ color: "#d03e50", textTransform: "none" }} startIcon={<DeleteIcon />}>Excluir</Button>
            ) : <span />}
            <div className={classes.footRight}>
              <Button onClick={onCancel} disabled={loading} variant="outlined" style={{ textTransform: "none" }}>Cancelar</Button>
              <Button type="submit" disabled={loading} className={classes.primary}>{loading ? <CircularProgress size={19} color="inherit" /> : "Salvar alterações"}</Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export function PlansManagerGrid({ records, onSelect, onNew, onDelete, query = "", visibility = "all" }) {
  const classes = useStyles();
  const plans = (records || []).filter((plan) => {
    const matchesQuery = (plan.name || "").toLowerCase().includes(query.toLowerCase());
    const matchesVisibility = visibility === "all" || (visibility === "public" ? plan.isPublic !== false : plan.isPublic === false);
    return matchesQuery && matchesVisibility;
  });
  return (
    <div className={classes.cards}>
      {plans.map((plan, index) => {
        const publicPlan = plan.isPublic !== false;
        const iconColors = ["#e9f1ff", "#fff4e4", "#eaf4ff", "#eff0ff"];
        const enabled = FEATURES.filter((feature) => plan[feature.key] !== false);
        return (
          <div className={classes.card} key={plan.id}>
            <div className={classes.cardTop}>
              <div className={classes.planIcon} style={{ background: iconColors[index % iconColors.length] }}><LayersIcon style={{ fontSize: 28 }} /></div>
              <div className={classes.cardMeta}>
                <span className={classes.planBadge} style={{ background: publicPlan ? "#e5f8ed" : "#fff0e4", color: publicPlan ? "#15915d" : "#b85815" }}>
                  {publicPlan ? <PublicIcon style={{ fontSize: 12, marginRight: 4 }} /> : <LockIcon style={{ fontSize: 12, marginRight: 4 }} />}
                  {publicPlan ? "Público" : "Privado"}
                </span>
                <div className={classes.planName}>{plan.name || "Plano sem nome"}</div>
              </div>
              <Tooltip title="Opções do plano"><IconButton size="small" onClick={() => onSelect(plan)} aria-label="Editar plano"><MoreIcon /></IconButton></Tooltip>
            </div>
            <div className={classes.price}>R$ {amountLabel(plan.amount)}<span className={classes.priceSuffix}>/mês</span></div>
            <div className={classes.limits}>
              {[{ icon: PeopleIcon, value: plan.users, label: "usuários" }, { icon: LinkIcon, value: plan.connections, label: "conexões" }, { icon: QueueIcon, value: plan.queues, label: "filas" }].map(({ icon: Icon, value, label }) => (
                <div className={classes.limitCell} key={label}>
                  <Icon style={{ color: BLUE, fontSize: 19, flexShrink: 0 }} />
                  <span><span className={classes.limitValue}>{value === null || value === undefined ? "–" : value}</span><br/><span className={classes.limitLabel}>{label}</span></span>
                </div>
              ))}
            </div>
            <div className={classes.includedLabel}>Recursos incluídos</div>
            <div className={classes.channelIcons}>
              {enabled.slice(0, 5).map(({ icon: Icon, color, label }) => (
                <Tooltip title={label} key={label}><span><Icon style={{ color, fontSize: 22 }} /></span></Tooltip>
              ))}
              {enabled.length > 5 && <span style={{ background: "#edf2ff", color: BLUE, fontWeight: 700, borderRadius: 14, padding: "4px 7px", fontSize: 11 }}>+{enabled.length - 5}</span>}
              {enabled.length === 0 && <span className={classes.smallText}>Nenhum recurso habilitado</span>}
            </div>
            <div className={classes.actions}>
              <Button className={classes.editButton} onClick={() => onSelect(plan)} startIcon={<EditIcon />}>Editar</Button>
              <Tooltip title="Excluir plano"><IconButton className={classes.moreButton} onClick={() => onDelete(plan)} aria-label="Excluir plano"><MoreIcon /></IconButton></Tooltip>
            </div>
          </div>
        );
      })}
      {!plans.length && <div className={classes.empty}>Nenhum plano corresponde aos filtros.</div>}
      <div className={classes.createCard} role="button" tabIndex={0} onClick={onNew} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNew(); } }}>
        <span style={{ width: 58, height: 58, borderRadius: "50%", background: "#eaf1ff", color: BLUE, display: "flex", alignItems: "center", justifyContent: "center" }}><AddIcon style={{ fontSize: 31 }} /></span>
        <strong style={{ color: "#152747", fontSize: 16 }}>Criar novo plano</strong>
        <span style={{ textAlign: "center", fontSize: 13, maxWidth: 230 }}>Adicione um plano e defina os limites e funcionalidades.</span>
        <Button onClick={(e) => { e.stopPropagation(); onNew(); }} variant="outlined" color="primary" startIcon={<AddIcon />} style={{ textTransform: "none", borderRadius: 8 }}>Novo plano</Button>
      </div>
    </div>
  );
}

export default function PlansManager() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorMode, setEditorMode] = useState(null); // "create" modal, "edit" painel lateral
  const [selectedPlan, setSelectedPlan] = useState({ ...EMPTY_PLAN });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [query, setQuery] = useState("");
  const [visibility, setVisibility] = useState("all");

  const loadPlans = async () => {
    try {
      const planList = await list();
      setRecords(Array.isArray(planList) ? planList : []);
    } catch (error) {
      toast.error("Não foi possível carregar a lista de registros");
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setSelectedPlan({ ...EMPTY_PLAN }); setEditorMode("create"); };
  const openEdit = (plan) => { setSelectedPlan(planForEdit(plan)); setEditorMode("edit"); };
  const closeEditor = () => { if (!loading) setEditorMode(null); };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (values.id !== undefined) await update(values);
      else await save(values);
      await loadPlans();
      setEditorMode(null);
      toast.success("Operação realizada com sucesso!");
    } catch (error) {
      toast.error("Não foi possível realizar a operação. Verifique se já existe um plano com o mesmo nome ou se os campos foram preenchidos corretamente");
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setLoading(true);
    try {
      await remove(pendingDelete.id);
      await loadPlans();
      setPendingDelete(null);
      setEditorMode(null);
      toast.success("Operação realizada com sucesso!");
    } catch (error) {
      toast.error("Não foi possível realizar a operação");
    } finally { setLoading(false); }
  };

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={`${classes.surface} ${classes.shell}`}>
        <div className={classes.head}>
          <div><h2 className={classes.heading}>Planos do sistema</h2><div className={classes.subheading}>Crie e gerencie os planos disponíveis para as empresas.</div></div>
          <Button className={classes.primary} onClick={openNew} startIcon={<AddIcon />}>Novo plano</Button>
        </div>
        <div className={classes.filters}>
          <TextField className={classes.search} variant="outlined" size="small" placeholder="Buscar plano pelo nome..." value={query} onChange={e => setQuery(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
          <TextField select label="Visibilidade" className={classes.selectFilter} variant="outlined" size="small" value={visibility} onChange={e => setVisibility(e.target.value)}>
            <MenuItem value="all">Todos os planos</MenuItem><MenuItem value="public">Públicos</MenuItem><MenuItem value="private">Privados</MenuItem>
          </TextField>
        </div>
        <PlansManagerGrid records={records} onSelect={openEdit} onNew={openNew} onDelete={setPendingDelete} query={query} visibility={visibility} />
      </Paper>
      <Dialog open={editorMode === "create"} onClose={closeEditor} fullWidth maxWidth="sm" classes={{ paper: classes.dialogPaper }} aria-labelledby="new-plan-title">
        <DialogTitle disableTypography id="new-plan-title"><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}><div><Typography variant="h6">Cadastrar plano</Typography><Typography variant="body2" color="textSecondary">Defina os limites e os recursos do novo plano.</Typography></div><IconButton onClick={closeEditor}><CloseIcon /></IconButton></div></DialogTitle>
        <PlanManagerForm initialValue={editorMode === "create" ? selectedPlan : EMPTY_PLAN} onSubmit={handleSubmit} onCancel={closeEditor} loading={loading} onDelete={setPendingDelete} />
      </Dialog>
      <Drawer anchor="right" open={editorMode === "edit"} onClose={closeEditor} classes={{ paper: classes.drawerPaper }}>
        <div className={classes.drawerHeader}><div><Typography variant="h6">Editar plano</Typography><Typography variant="body2" color="textSecondary">Atualize os dados e recursos do plano.</Typography></div><IconButton onClick={closeEditor}><CloseIcon /></IconButton></div>
        <PlanManagerForm initialValue={editorMode === "edit" ? selectedPlan : EMPTY_PLAN} onSubmit={handleSubmit} onCancel={closeEditor} onDelete={setPendingDelete} loading={loading} />
      </Drawer>
      <ConfirmationModal title="Exclusão de Registro" open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)} onConfirm={handleDelete}>Deseja realmente excluir esse registro?</ConfirmationModal>
    </div>
  );
}
